import { checkOrganizationNameDuplicateApi, updateOrganizationApi, useOrganizationDetail } from "@/api/services/admin/organization";
import { TDuplicatedOrganization } from "@/api/services/admin/organization/dtos/check-organization-name.dto";
import { TUpdateOrganizationFormData } from "@/api/services/admin/organization/dtos/update-organization.dto";
import { IconSave } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { MenuNavigate, TMenuContent } from "@/components/elements/MenuNavigate/MenuNavigate";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { DuplicateOrganizationNameModal } from "@/features/organization/components/DuplicateOrganizationNameModal";
import { OrganizationEditBasicInfo } from "@/features/organization/components/organization-edit/OrganizationEditBasicInfo";
import { OrganizationEditOtherInfo } from "@/features/organization/components/organization-edit/OrganizationEditOtherInfo";
import { OrganizationEditProductSelect } from "@/features/organization/components/organization-edit/OrganizationEditProductSelect";
import { updateOrganizationSchema } from "@/features/organization/schema/updateOrganizationSchema";
import { convertUpdateOrganizationFormData } from "@/features/organization/utils/convertOrganizationFormData";
import { useToast } from "@/hooks/useToast";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

const OrganizationEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const skipBlockerRef = useRef(false);
  const pendingFormDataRef = useRef<TUpdateOrganizationFormData | null>(null);
  const duplicateNameModal = useVisible();
  const [duplicates, setDuplicates] = useState<TDuplicatedOrganization[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading } = useOrganizationDetail();
  const organization = data?.data;

  const methods = useForm<TUpdateOrganizationFormData>({
    defaultValues: {
      name: "",
      province: "",
      municipality: "",
      crmLink: "",
      adminInfo: "",
      adminNotes: "",
      // 担当者 Select holds the user id as a string ("" = none).
      personInChargeId: "",
    },
    resolver: yupResolver(updateOrganizationSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control, reset, setValue, getValues } = methods;

  const nameValue = useWatch({ control, name: "name" });
  const selectedProductIds = useWatch({ control, name: "productIds" }) ?? [];

  useEffect(() => {
    if (!organization) return;
    reset({
      name: organization.name ?? "",
      province: organization.province ?? "",
      municipality: organization.municipality ?? "",
      crmLink: organization.crmLink ?? "",
      adminInfo: organization.adminInfo ?? "",
      adminNotes: organization.adminNotes ?? "",
      productIds: organization?.productContracts?.map((item) => item.productId),
      personInChargeId: organization.personInChargeId != null ? String(organization.personInChargeId) : "",
    });
  }, [organization, reset]);

  const runUpdate = useCallback(
    async (formData: TUpdateOrganizationFormData) => {
      if (!id) return;
      setIsSubmitting(true);
      try {
        await updateOrganizationApi(Number(id), convertUpdateOrganizationFormData(formData));
        toast.success(API_SUCCESS_MESSAGE);
        skipBlockerRef.current = true;
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_DETAIL] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_LIST] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRODUCT_CONTRACT.FETCH_LIST_BY_ORGANIZATION] });
        navigate(getDynamicRoute(AppRoutes.organizationDetail, { id }));
      } catch (error) {
        setErrorForm(methods, error);
        showError(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [id, toast, queryClient, navigate, methods],
  );

  const onSubmit = useCallback(
    async (formData: TUpdateOrganizationFormData) => {
      if (!id) return;
      const enteredName = formData.name?.trim() ?? "";
      const originalName = organization?.name?.trim() ?? "";
      if (enteredName && enteredName !== originalName) {
        try {
          const result = await checkOrganizationNameDuplicateApi(enteredName);
          const others = result.duplicates.filter((dup) => dup.id !== Number(id));
          if (others.length > 0) {
            pendingFormDataRef.current = formData;
            setDuplicates(others);
            duplicateNameModal.open();
            return;
          }
        } catch (error) {
          showError(error);
          return;
        }
      }
      await runUpdate(formData);
    },
    [id, organization, duplicateNameModal, runUpdate],
  );

  const handleConfirmDuplicate = useCallback(async () => {
    if (!pendingFormDataRef.current) return;
    duplicateNameModal.close();
    await runUpdate(pendingFormDataRef.current);
  }, [duplicateNameModal, runUpdate]);

  const handleBack = () => {
    navigate(getDynamicRoute(AppRoutes.organizationDetail, { id }));
  };

  const handleToggleProduct = useCallback(
    (productId: number) => {
      const currentIds = getValues("productIds") ?? [];
      const newIds = currentIds.includes(productId) ? currentIds.filter((id) => id !== productId) : [...currentIds, productId];
      setValue("productIds", newIds, { shouldDirty: true });
    },
    [getValues, setValue],
  );

  const contents: TMenuContent[] = useMemo(
    () => [
      {
        title: "基本情報",
        content: (
          <OrganizationEditBasicInfo
            control={control}
            getValues={getValues}
            setValue={setValue}
            externalId={organization?.externalId ?? ""}
            status={organization?.status ?? ""}
            organizationType={organization?.type ?? ""}
            currentPersonInCharge={organization?.personInCharge}
          />
        ),
      },
      {
        title: "その他情報（内部用）",
        content: <OrganizationEditOtherInfo control={control} />,
      },
      {
        title: "契約対象",
        content: <OrganizationEditProductSelect selectedProductIds={selectedProductIds} onToggleProduct={handleToggleProduct} />,
      },
    ],
    [control, getValues, setValue, organization, selectedProductIds, handleToggleProduct],
  );

  if (isLoading) return <LoadingPage />;
  if (!organization) return null;

  return (
    <Form
      height="100%"
      methods={methods}
      onSubmit={onSubmit}
      schema={updateOrganizationSchema}
      shouldBlock={({ currentLocation, nextLocation }) => {
        if (skipBlockerRef.current) return false;
        if (currentLocation.pathname === nextLocation.pathname) return false;
        return true;
      }}
    >
      <Stack gap={3}>
        <Stack gap={2}>
          <Typography variant="head20Bold" color={text.primary}>
            組織情報の編集
          </Typography>
          <Typography variant="body14" color={text.secondary}>
            組織の基本情報および契約情報を変更できます。ステータスはトライアル終了日や解約日の翌日に自動的に変更されます。
          </Typography>
        </Stack>

        <MenuNavigate
          menuPrefix={
            <Typography
              variant="body14Bold"
              color={text.primary}
              px={2}
              pb={1}
              sx={{
                wordBreak: "break-word",
              }}
            >
              {nameValue ?? ""}
            </Typography>
          }
          contents={contents}
          contentAffix={
            <StickyFooter>
              <Stack width="100%" justifyContent="space-between" flexDirection="row">
                <Button size="sm" variant="ghost" onClick={handleBack}>
                  戻る
                </Button>
                <Button size="sm" type="submit" sx={{ gap: 1 }}>
                  <IconSave color="currentColor" />
                  保存
                </Button>
              </Stack>
            </StickyFooter>
          }
        />
      </Stack>

      <DuplicateOrganizationNameModal
        open={duplicateNameModal.visible}
        duplicates={duplicates}
        isLoading={isSubmitting}
        onClose={duplicateNameModal.close}
        onConfirm={handleConfirmDuplicate}
      />
    </Form>
  );
};

export default OrganizationEditPage;
